import type { BillingProduct } from '@/lib/billing/product-registry';
import { PLAN_MONTHLY_CREDITS } from '@/lib/constants/plan-credits';
import type { Message } from '@/lib/types/chat';

export const RATE_LIMIT_CARDS = ['credits', 'credits_upgrade', 'credits_paused'] as const;
export const MODEL_CARDS = [
  'intelligence',
  'reasoning',
  'model_picker',
  'model_picker_menu',
  'model_picker_inline'
] as const;
export const FREE_CARDS = ['upgrade', ...MODEL_CARDS] as const;
export const PLAN_CREDIT_CARDS = ['more_credits', 'credits_usage', 'more_credits_short'] as const;
// The timed offer bar above the chat toolbar; never part of the in-chat card rotation.
export const OFFER_BANNER_CARD = 'offer_banner';

export type ChatUpgradeCardKind =
  | (typeof RATE_LIMIT_CARDS)[number]
  | (typeof FREE_CARDS)[number]
  | (typeof PLAN_CREDIT_CARDS)[number]
  | typeof OFFER_BANNER_CARD;

export function getChatUpgradeCardType(kind: ChatUpgradeCardKind) {
  if ((RATE_LIMIT_CARDS as readonly string[]).includes(kind)) return 'rate_limit';
  return kind === OFFER_BANNER_CARD ? 'offer_banner' : 'mid_stream';
}

export type ChatUpgradePlan = { name: string; monthlyCredits: number };

// Ordered by monthly price, so "next plan up" never points at a cheaper plan
// (e.g. Ultra goes to Growth, skipping the cheaper Business plan). Growth3 is the top plan.
const PLAN_LADDERS: Record<'personal' | 'team', readonly BillingProduct[]> = {
  personal: ['lite', 'pro', 'max', 'ultra', 'growth', 'growth2', 'growth3'],
  team: ['team', 'business', 'growth', 'growth2', 'growth3']
};

// Product slugs as plan names (product `pro` is Pro and `max` is Max), not the legacy display names.
const PLAN_NAMES: Record<BillingProduct, string> = {
  lite: 'Lite',
  pro: 'Pro',
  max: 'Max',
  ultra: 'Ultra',
  team: 'Team',
  business: 'Business',
  growth: 'Growth',
  growth2: 'Growth2',
  growth3: 'Growth3',
  growth4: 'Growth4'
};

// Team credits mirror components/settings/business-plan-config.ts.
const MONTHLY_CREDITS: Partial<Record<BillingProduct, number>> = {
  lite: PLAN_MONTHLY_CREDITS.Lite,
  // PLAN_MONTHLY_CREDITS is keyed by the legacy display names.
  pro: PLAN_MONTHLY_CREDITS.Standard,
  max: PLAN_MONTHLY_CREDITS.Pro,
  ultra: PLAN_MONTHLY_CREDITS.Ultra,
  business: 60_000,
  growth: 100_000,
  growth2: 200_000,
  growth3: 400_000
};

// Strongest Julius model each plan unlocks (see the Julius model registry's allowed plans).
const JULIUS_MODELS = ['Julius 1.5 Lite', 'Julius 1.5 Max', 'Julius 1.5 Ultra'] as const;
const TOP_MODEL: Record<BillingProduct | 'free', number> = {
  free: 0,
  lite: 1,
  pro: 1,
  team: 1,
  max: 2,
  ultra: 2,
  business: 2,
  growth: 2,
  growth2: 2,
  growth3: 2,
  growth4: 2
};

// Max and every plan priced above it only see billboards when they run out of credits.
const NO_MID_STREAM_CARD_PRODUCTS: readonly BillingProduct[] = [
  'max',
  'ultra',
  'business',
  'growth',
  'growth2',
  'growth3',
  'growth4'
];

function getLadder(product?: string | null) {
  if (!product) return { ladder: PLAN_LADDERS.personal, index: -1 };
  const ladder = PLAN_LADDERS.team.includes(product as BillingProduct)
    ? PLAN_LADDERS.team
    : PLAN_LADDERS.personal;
  return { ladder, index: ladder.indexOf(product as BillingProduct) };
}

const toPlan = (product: BillingProduct): ChatUpgradePlan | null => {
  const monthlyCredits = MONTHLY_CREDITS[product];
  return monthlyCredits ? { name: PLAN_NAMES[product], monthlyCredits } : null;
};

// The next plan up in price and its credits; null on the top plan or unknown plans.
export function getNextPlanUpgrade(
  product?: string | null
): { current: ChatUpgradePlan; next: ChatUpgradePlan } | null {
  const { ladder, index } = getLadder(product);
  if (index < 0) return null;
  const current = toPlan(ladder[index]);
  const next = ladder[index + 1] && toPlan(ladder[index + 1]);
  return current && next ? { current, next } : null;
}

// The cheapest plan above the user's that unlocks a stronger Julius model.
// Free users start from Pro.
export function getNextModelUpgrade(product?: string | null) {
  const { ladder, index } = getLadder(product);
  if (product && index < 0) return null;
  const currentRank = product ? TOP_MODEL[ladder[index]] : TOP_MODEL.free;
  const start = product ? index + 1 : ladder.indexOf('pro');
  const plan = ladder.slice(start).find((candidate) => TOP_MODEL[candidate] > currentRank);
  return plan ? { planName: PLAN_NAMES[plan], model: JULIUS_MODELS[TOP_MODEL[plan]] } : null;
}

// Julius models the user's next plan unlocks (e.g. only Julius 1.5 Max for free users), weakest first.
export function getLockedJuliusModels(product?: string | null): string[] {
  const rank = TOP_MODEL[(product ?? 'free') as BillingProduct | 'free'] ?? TOP_MODEL.free;
  const next = getNextModelUpgrade(product);
  return next ? JULIUS_MODELS.slice(rank + 1, JULIUS_MODELS.indexOf(next.model) + 1) : [];
}

function hashSeed(value: string) {
  let hash = 2166136261;
  for (const char of value) {
    hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  }
  // FNV's low bits are only character parity; mix them so experiments are independent.
  hash = Math.imul(hash ^ (hash >>> 16), 0x85ebca6b);
  hash = Math.imul(hash ^ (hash >>> 13), 0xc2b2ae35);
  return (hash ^ (hash >>> 16)) >>> 0;
}

// Stable bucket, so a user keeps the same variant for the whole chat.
export function pickChatUpgradeVariant<T>(
  experiment: string,
  seed: string,
  variants: readonly T[]
) {
  return variants[hashSeed(`${experiment}:${seed}`) % variants.length];
}

// A random card per response that never repeats the previous response's card. Each step is
// derived from the chat seed, so re-renders of the same response keep showing the same card.
export function rotateChatUpgradeVariant<T>(
  experiment: string,
  seed: string,
  variants: readonly T[],
  turn: number
) {
  let index = hashSeed(`${experiment}:${seed}`) % variants.length;
  for (let step = 2; step <= turn && variants.length > 1; step += 1) {
    const jump = 1 + (hashSeed(`${experiment}:${seed}:${step}`) % (variants.length - 1));
    index = (index + jump) % variants.length;
  }
  return variants[index];
}

// Cards wait this long after the user sends a message before appearing.
export const CHAT_UPGRADE_CARD_DELAY_MS = 3000;

// The latest prompt and when it was sent; the optimistic row is stamped at send time.
export function getLastPrompt(messages: readonly Partial<Message>[]) {
  const prompt = messages.findLast(
    (message) => message.role === 'user' && message.flags !== 'compaction_summary'
  );
  const sentAt = prompt?.completed_at ? Date.parse(prompt.completed_at) : Number.NaN;
  return { id: prompt?.message_id ?? null, sentAt: Number.isFinite(sentAt) ? sentAt : 0 };
}

// Count prompts, not tool calls or the multiple assistant rows in one response.
// The optimistic user row is included, so a new send removes the old card unless credits are exhausted.
export function getChatUpgradeCard({
  messages,
  isFreeUser,
  isPaidUser,
  isTurnActive = false,
  availableBalance,
  variantSeed = '',
  hasUltraAccess,
  product
}: {
  messages: readonly Partial<Message>[];
  isFreeUser: boolean;
  isPaidUser: boolean;
  isTurnActive?: boolean;
  availableBalance?: number;
  variantSeed?: string;
  hasUltraAccess?: boolean;
  product?: string | null;
}): ChatUpgradeCardKind | null {
  const isOutOfCredits = availableBalance != null && availableBalance <= 0;
  if (!isFreeUser && !isPaidUser) return null;
  // Exhaustion takes priority over cadence, model pitches, and waiting for response text.
  if (isOutOfCredits) {
    return isFreeUser || getNextPlanUpgrade(product)
      ? pickChatUpgradeVariant('credits', variantSeed, RATE_LIMIT_CARDS)
      : null;
  }
  if (!isFreeUser && NO_MID_STREAM_CARD_PRODUCTS.includes(product as BillingProduct)) return null;

  let promptCount = 0;
  let lastPromptIndex = -1;
  messages.forEach((message, index) => {
    if (message.role === 'user' && message.flags !== 'compaction_summary') {
      promptCount += 1;
      lastPromptIndex = index;
    }
  });
  if (promptCount === 0 || (!isFreeUser && promptCount % 3 !== 0)) return null;

  const freeCard = rotateChatUpgradeVariant('ad', variantSeed, FREE_CARDS, promptCount);

  // Free ads start with generation, including the thinking/tool-only phase.
  // Waiting for an assistant text row can otherwise delay the card until completion.
  if (isFreeUser && isTurnActive) return freeCard;

  // Show as soon as text arrives, then retain it with the completed response.
  const responseStarted = messages
    .slice(lastPromptIndex + 1)
    .some(
      (message) =>
        message.role === 'assistant' &&
        message.flags !== 'compaction_summary' &&
        !message.thinking_call &&
        !message.function_call &&
        Boolean(message.content || message.display)
    );
  if (!responseStarted) return null;
  if (isFreeUser) return freeCard;

  // Model pitches only make sense without Julius Ultra; credit pitches go to every plan below the top.
  if (hasUltraAccess == null) return null;
  const paidCards = [
    ...(hasUltraAccess ? [] : MODEL_CARDS),
    ...(getNextPlanUpgrade(product) ? PLAN_CREDIT_CARDS : [])
  ];
  return paidCards.length
    ? rotateChatUpgradeVariant('paid', variantSeed, paidCards, promptCount / 3)
    : null;
}
